import React from "react";
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from "@mui/joy";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/joy/styles";

import DonationSummary from "../components/Donation/DonationSummary";
import DonationTable from "../components/Donation/DonationTable";
import DonationMobile from "../components/Donation/DonationMobile";
import { useDonationsStore } from "../store/useDonationsStore";

export default function Donation() {
  const { refresh } = useDonationsStore();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const heroBackground = isDark
    ? "linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)"
    : "radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)";
  const heroOverlay = isDark
    ? "radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)"
    : "radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)";
  const heroTitle = isDark ? "rgba(248,250,252,0.94)" : "text.primary";
  const heroBody = isDark ? "rgba(226,232,240,0.8)" : "text.secondary";
  const panelWarm = isDark
    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))"
    : "linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))";
  const panelCool = isDark
    ? "linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))"
    : "linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))";

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <Stack spacing={3} sx={{ pb: 2 }}>
          <Card
            variant="outlined"
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: "xl",
              borderColor: "divider",
              boxShadow: "lg",
              background: heroBackground,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: heroOverlay,
                opacity: 0.9,
                pointerEvents: "none",
              }}
            />
            <CardContent sx={{ position: "relative" }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
                  <Stack spacing={0.75}>
                    <Chip size="sm" variant="soft" color="neutral" sx={{ alignSelf: "flex-start", fontWeight: 600 }}>
                      Every offering counts
                    </Chip>
                    <Typography level="h2" sx={{ fontWeight: 800, color: heroTitle }}>
                      Donations
                    </Typography>
                    <Typography level="body-sm" sx={{ maxWidth: 680, color: heroBody }}>
                      Celebrate and track the heartfelt contributions that sustain ISKCON Kanpur’s seva, outreach, and prasadam distribution.
                    </Typography>
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button
                      component={RouterLink}
                      to="/donate"
                      startDecorator={<VolunteerActivismRoundedIcon />}
                      size="md"
                      variant="soft"
                    >
                      Record a new offering
                    </Button>
                    <Button
                      variant="plain"
                      endDecorator={<ArrowForwardRoundedIcon />}
                      component="a"
                      href="https://iskconkanpur.org"
                      target="_blank"
                      rel="noreferrer"
                    >
                      View donor statement
                    </Button>
                  </Stack>
                </Stack>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <Card
                      variant="soft"
                      sx={{
                        flex: 1,
                        borderRadius: "lg",
                        background: panelWarm,
                        boxShadow: "sm",
                      }}
                    >
                    <CardContent>
                      <Typography level="title-sm" sx={{ color: "text.secondary" }}>
                        Stewardship lens
                      </Typography>
                      <Typography level="title-lg" sx={{ mt: 0.5 }}>
                        Track seva momentum
                      </Typography>
                      <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                        Keep every offering mapped to purpose, head, and donor.
                      </Typography>
                    </CardContent>
                  </Card>
                    <Card
                      variant="soft"
                      sx={{
                        flex: 1,
                        borderRadius: "lg",
                        background: panelCool,
                        boxShadow: "sm",
                      }}
                    >
                    <CardContent>
                      <Typography level="title-sm" sx={{ color: "text.secondary" }}>
                        Encourage seva
                      </Typography>
                      <Typography level="title-lg" sx={{ mt: 0.5 }}>
                        Celebrate generosity
                      </Typography>
                      <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                        Recognize devotees and follow up with receipts in time.
                      </Typography>
                    </CardContent>
                  </Card>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <DonationSummary />

          <DonationMobile />
          <DonationTable />

          <Button
            variant="soft"
            color="primary"
            startDecorator={<DownloadRoundedIcon />}
            sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
            onClick={() => window.open("/api/devotee/donation/export", "_blank")}
          >
            Export full donation ledger
          </Button>
      </Stack>
    </Box>
  );
}
